import { useState, useEffect } from "react";
import API from "../api";

export default function Marketing() {

  const [search, setSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 🔥 NEW STATES (ADDED ONLY)
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    name: "",
    platform: "",
    leads: 0
  });

  /* ===============================
     FETCH FROM BACKEND (UNCHANGED)
  =============================== */
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        const res = await fetch(`${API}/marketing`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to load campaigns");
        }

        setCampaigns(data.data || []);

      } catch (err) {
        console.error("❌ Marketing error:", err);
        setError("Failed to load campaigns");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, []);

  /* ===============================
     🔥 CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      if (!form.name || !form.platform) {
        return alert("Fill all fields");
      }

      const url = editMode
        ? `${API}/marketing/${form.id}`
        : `${API}/marketing`;

      const method = editMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: form.name,
          platform: form.platform,
          leads: form.leads,
          status: "active"
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      if (editMode) {
        setCampaigns(prev =>
          prev.map(c =>
            c.id === form.id ? { ...form, status: "active" } : c
          )
        );
      } else {
        setCampaigns(prev => [
          { ...form, id: Date.now(), status: "active" },
          ...prev
        ]);
      }

      resetForm();

    } catch (err) {
      console.error("❌ Save error:", err);
      alert("Error saving campaign");
    }
  };

  /* ===============================
     🔥 DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete campaign?")) return;

    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await fetch(`${API}/marketing/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setCampaigns(prev => prev.filter(c => c.id !== id));

    } catch (err) {
      console.error("❌ Delete error:", err);
    }
  };

  /* ===============================
     🔥 EDIT
  =============================== */
  const handleEdit = (c) => {
    setForm(c);
    setEditMode(true);
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({ id: null, name: "", platform: "", leads: 0 });
    setEditMode(false);
    setShowModal(false);
  };

  /* ===============================
     FILTER (UNCHANGED)
  =============================== */
  const filteredCampaigns = campaigns.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ===============================
     KPI CALCULATIONS (UNCHANGED)
  =============================== */
  const totalCampaigns = campaigns.length;
  const activeCampaigns = campaigns.filter(c => c.status === "active").length;
  const totalLeads = campaigns.reduce((sum, c) => sum + (c.leads || 0), 0);

  return (
    <div className="space-y-6">

      {/* HEADER (UPDATED BUTTON ONLY) */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold">Marketing</h2>
          <p className="text-gray-500">
            Manage campaigns and track performance
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
        >
          + Create Campaign
        </button>
      </div>

      {/* KPI CARDS (UNCHANGED) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h4>Total Campaigns</h4>
          <p className="text-2xl">{loading ? "..." : totalCampaigns}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h4>Active Campaigns</h4>
          <p className="text-2xl text-green-600">
            {loading ? "..." : activeCampaigns}
          </p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h4>Leads Generated</h4>
          <p className="text-2xl">{loading ? "..." : totalLeads}</p>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h4>Conversion Rate</h4>
          <p className="text-2xl text-blue-600">18%</p>
        </div>
      </div>

      {/* ERROR (UNCHANGED) */}
      {error && (
        <div className="bg-red-100 text-red-600 p-3 rounded">
          {error}
        </div>
      )}

      {/* SEARCH (UNCHANGED) */}
      <div className="bg-white p-4 rounded-xl shadow-sm border">
        <input
          type="text"
          placeholder="Search campaigns..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full p-3 border rounded-lg"
        />
      </div>

      {/* TABLE (UPDATED: ACTIONS ADDED ONLY) */}
      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full text-sm">

          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-4">Campaign</th>
              <th className="p-4">Platform</th>
              <th className="p-4">Leads</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th> {/* 🔥 ADDED */}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="p-6 text-center">
                  Loading campaigns...
                </td>
              </tr>
            ) : filteredCampaigns.length > 0 ? (
              filteredCampaigns.map((c) => (
                <tr key={c.id} className="border-t hover:bg-gray-50">

                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4">{c.platform}</td>
                  <td className="p-4">{c.leads}</td>

                  <td className="p-4">{c.status}</td>

                  <td className="p-4 flex gap-2">
                    <button
                      onClick={() => handleEdit(c)}
                      className="text-blue-600"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(c.id)}
                      className="text-red-600"
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="p-6 text-center text-gray-500">
                  No campaigns found
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      {/* 🔥 MODAL ADDED */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3 className="text-lg font-bold">
              {editMode ? "Edit Campaign" : "Create Campaign"}
            </h3>

            <input
              placeholder="Name"
              className="w-full p-2 border"
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
            />

            <input
              placeholder="Platform"
              className="w-full p-2 border"
              value={form.platform}
              onChange={(e) =>
                setForm({ ...form, platform: e.target.value })
              }
            />

            <input
              type="number"
              placeholder="Leads"
              className="w-full p-2 border"
              value={form.leads}
              onChange={(e) =>
                setForm({ ...form, leads: e.target.value })
              }
            />

            <div className="flex justify-end gap-2">
              <button onClick={resetForm}>Cancel</button>

              <button
                onClick={handleSubmit}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                {editMode ? "Update" : "Create"}
              </button>
            </div>

          </div>
        </div>
      )}

      {/* SUMMARY (UNCHANGED) */}
      <div className="bg-white p-6 rounded-xl shadow-sm border">
        <h3 className="text-lg font-semibold mb-4">
          Marketing Insights
        </h3>

        <div className="flex flex-col md:flex-row justify-between text-sm text-gray-600 gap-4">
          <p>✔ Best performing platform: Google Ads</p>
          <p>✔ Highest conversion: Email Campaign</p>
          <p>✔ Growth this month: +22%</p>
        </div>
      </div>

    </div>
  );
}